import {ListWarehouseModelsQuery} from './__generated/ListWarehouseModelsQuery';

type WarehouseColumnNode =
  ListWarehouseModelsQuery['warehouseModelListing']['warehouseModels'][number]['tables'][number]['columns'][number];

export enum WarehouseColumnCategory {
  ID = 'ID',
  RELATIONAL_USER_DEFINED = 'RELATIONAL_USER_DEFINED',
  RELATIONAL_SYSTEM_DEFINED = 'RELATIONAL_SYSTEM_DEFINED',
  NON_RELATIONAL_USER_DEFINED = 'NON_RELATIONAL_USER_DEFINED',
  NON_RELATIONAL_SYSTEM_DEFINED = 'NON_RELATIONAL_SYSTEM_DEFINED',
  INTERNAL_SYSTEM = 'INTERNAL_SYSTEM',
}

export function columnToCategory(column: WarehouseColumnNode): WarehouseColumnCategory {
  const isRelational = column.columnRelationships.length > 0;
  const userDefined = !column.name.endsWith('$');

  if (column.name === 'id') {
    return WarehouseColumnCategory.ID;
  } else if (column.name.startsWith('_') || column.name === 'source_id') {
    return WarehouseColumnCategory.INTERNAL_SYSTEM;
  } else if (isRelational && userDefined) {
    return WarehouseColumnCategory.RELATIONAL_USER_DEFINED;
  } else if (isRelational && !userDefined) {
    return WarehouseColumnCategory.RELATIONAL_SYSTEM_DEFINED;
  } else if (!isRelational && userDefined) {
    return WarehouseColumnCategory.NON_RELATIONAL_USER_DEFINED;
  } else {
    return WarehouseColumnCategory.NON_RELATIONAL_SYSTEM_DEFINED;
  }
}

/**
 * Sort a list of WarehouseColumnData to be in a more user-friendly order.
 *
 * Categorizes columns as being an ID column, a underscore-starting column (i.e. _pkey), or as relational/
 * nonrelational and user/system defined based. Assumes system-defined columns start with $.
 *
 * Then sorts in order of:
 * 1. ID column
 * 2. Relational user-defined columns
 * 3. Relational system-defined columns
 * 4. Non-relational user-defined columns
 * 5. Non-relational system-defined columns
 * 6. Underscore-starting columns
 *
 * Preserves original order within categories.
 */
export default function sortedTableColumns(columns: Array<WarehouseColumnNode>): Array<WarehouseColumnNode> {
  const partition: {[category in WarehouseColumnCategory]: Array<WarehouseColumnNode>} = {
    [WarehouseColumnCategory.ID]: [],
    [WarehouseColumnCategory.RELATIONAL_USER_DEFINED]: [],
    [WarehouseColumnCategory.RELATIONAL_SYSTEM_DEFINED]: [],
    [WarehouseColumnCategory.NON_RELATIONAL_USER_DEFINED]: [],
    [WarehouseColumnCategory.NON_RELATIONAL_SYSTEM_DEFINED]: [],
    [WarehouseColumnCategory.INTERNAL_SYSTEM]: [],
  };
  for (const column of columns) {
    const category = columnToCategory(column);
    partition[category].push(column);
  }
  return partition[WarehouseColumnCategory.ID]
    .concat(partition[WarehouseColumnCategory.RELATIONAL_USER_DEFINED])
    .concat(partition[WarehouseColumnCategory.RELATIONAL_SYSTEM_DEFINED])
    .concat(partition[WarehouseColumnCategory.NON_RELATIONAL_USER_DEFINED])
    .concat(partition[WarehouseColumnCategory.NON_RELATIONAL_SYSTEM_DEFINED])
    .concat(partition[WarehouseColumnCategory.INTERNAL_SYSTEM]);
}
